import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Search, FileText, ArrowUpRight, FileType, BookOpen } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Contact from "@/components/sections/Contact";
import { api, fileUrl } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";

const ALL_KEY = "__all__";

const typeIcon = (t) => {
  if (/present/i.test(t)) return FileType;
  if (/article|note/i.test(t)) return BookOpen;
  return FileText;
};

export default function KnowledgeHub() {
  const { t } = useLanguage();
  const k = t.knowledge;
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [topic, setTopic] = useState(ALL_KEY);

  useEffect(() => {
    api.get("/documents", { params: { section: "knowledge" } }).then((r) => setDocs(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return docs.filter((d) => {
      const matchTopic = topic === ALL_KEY || d.category === topic;
      const hay = `${d.title} ${d.description} ${(d.tags || []).join(" ")} ${d.category}`.toLowerCase();
      return matchTopic && hay.includes(q.toLowerCase());
    });
  }, [docs, q, topic]);

  const topicButtons = [{ key: ALL_KEY, label: k.allLabel }, ...k.topics];

  return (
    <>
      <main className="pt-32 md:pt-40 pb-24" data-testid="knowledge-page">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-auroraDeep mb-6">{k.label}</p>
            <h1 className="font-display font-extrabold tracking-tighter text-5xl md:text-7xl leading-[0.92] max-w-3xl">
              {k.titlePrefix}<span className="font-serif-accent italic font-medium">{k.titleHighlight}</span>{k.titleSuffix}
            </h1>
            <p className="mt-6 max-w-xl text-lg font-light text-stone leading-relaxed">
              {k.subtitle}
            </p>
          </Reveal>

          {/* Search + filter */}
          <Reveal delay={0.1}>
            <div className="mt-12 flex items-center gap-3 border border-ink px-5 py-3.5 max-w-xl bg-paper">
              <Search size={18} className="text-stone" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={k.searchPlaceholder}
                className="w-full bg-transparent outline-none font-light placeholder:text-stone/60"
                data-testid="knowledge-search"
              />
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {topicButtons.map((tp) => (
                <button
                  key={tp.key}
                  onClick={() => setTopic(tp.key)}
                  className={`font-mono-tech text-[11px] uppercase tracking-wider border px-3 py-1.5 transition-colors duration-300 ${topic === tp.key ? "bg-ink text-paper border-ink" : "border-line text-stone hover:border-ink"}`}
                  data-testid={`topic-${tp.key === ALL_KEY ? "all" : tp.key.toLowerCase().replace(/[\s&]+/g, "-")}`}
                >
                  {tp.label}
                </button>
              ))}
            </div>
          </Reveal>

          {/* Grid */}
          <div className="mt-14">
            {filtered.length === 0 ? (
              <div className="border border-dashed border-line p-16 text-center" data-testid="knowledge-empty">
                <p className="font-display text-2xl mb-2">{k.emptyTitle}</p>
                <p className="text-stone font-light">{k.emptyBody}</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((d, i) => {
                  const Icon = typeIcon(d.content_type);
                  const href = d.external_url || (d.file_path ? fileUrl(d.id) : null);
                  return (
                    <motion.a
                      key={d.id}
                      href={href || "#"}
                      target={href ? "_blank" : undefined}
                      rel="noreferrer"
                      initial={{ opacity: 0, y: 24 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: (i % 3) * 0.05 }}
                      className="group border border-line hover:border-ink bg-paper p-6 flex flex-col justify-between min-h-[220px] hover:-translate-y-1 transition-[transform,border-color] duration-300"
                      data-testid={`knowledge-card-${d.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <Icon size={20} className="text-auroraDeep" />
                        <span className="font-mono-tech text-[10px] uppercase tracking-[0.15em] text-stone">{d.content_type}</span>
                      </div>
                      <div className="mt-8">
                        <h3 className="font-display font-medium text-2xl leading-snug">{d.title}</h3>
                        {d.description && <p className="mt-2 text-sm font-light text-stone line-clamp-2">{d.description}</p>}
                      </div>
                      <div className="mt-6 flex items-center justify-between">
                        <span className="font-mono-tech text-[10px] uppercase tracking-wider text-stone">{d.category}</span>
                        <ArrowUpRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </motion.a>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
      <Contact />
    </>
  );
}
